import {useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router';
import useYDocStore from '../../../../stores/useYDocStore';
import {readSerializableHeaderFromYDoc} from '../../../../utils/documentHeaderMeta';
import {resolveDocumentAuthors} from '../../../../utils/resolveDocumentAuthors';
import {decodeYDocToProsemirror, extractReferencesFromYDoc} from '../../../../utils/yjsSnapshot';
import {useReportStore} from '../../useReportStore';

/**
 * Custom hook for toggling between preview and edit views
 *
 * @param {string} reportId - The report ID
 * @param {function} setContent - Function to update content in store
 * @param {function} getEditorContent - Function to get current editor content
 * @returns {object} View state and toggle handler
 */
export function useViewToggle(reportId, setContent, getEditorContent) {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine whether current path is preview or edit
    const pathSegs = (location.pathname || '').split('/').filter(Boolean);
    const lastSeg = pathSegs[pathSegs.length - 1] || '';
    const isPreview = lastSeg.toLowerCase() === 'preview';

    const handleViewToggle = useCallback(() => {
        // Toggle between /products/:reportId/edit and /products/:reportId/preview
        const target = reportId
            ? `/products/${reportId}/${isPreview ? 'edit' : 'preview'}`
            : (isPreview ? 'edit' : 'preview');

        // If navigating to preview, persist latest editor content to zustand first
        const goingToPreview = target.toLowerCase().endsWith('/preview') ||
            (!reportId && !isPreview && (target === 'preview'));

        if (goingToPreview) {
            const content = getEditorContent();
            if (content !== null) {
                try {
                    if (typeof setContent === 'function') {
                        setContent(content);
                    }
                    const ydoc = useYDocStore.getState().ydoc;
                    const references = ydoc ? extractReferencesFromYDoc(ydoc) : [];
                    const metadata = ydoc ? readSerializableHeaderFromYDoc(ydoc) : null;
                    // mapNode.js/yjsSnapshot.js ticket 06: the live editor's own PM doc JSON
                    // (`content` above) never carries map-node snapshotThumbUrl — the live
                    // collaborative MapNodeView ignores that attr and renders straight from the
                    // Yjs mapPreviews Y.Map. The preview page has no live Yjs doc to read from,
                    // so mirror the same mapPreviews -> snapshotThumbUrl sync other static
                    // renders already use (decodeYDocToProsemirror) before staging the draft,
                    // rather than staging the un-synced doc and showing a false "unavailable"
                    // placeholder for maps that do have a captured preview.
                    let previewContent = content;
                    if (ydoc) {
                        try {
                            previewContent = decodeYDocToProsemirror(ydoc);
                        } catch (err) {
                            console.warn('[useViewToggle] failed to sync map previews for preview draft', err);
                        }
                    }
                    const reportIdStr = String(reportId);
                    useReportStore.getState().setPreviewDraft({
                        reportId: reportIdStr,
                        content: previewContent,
                        references,
                        metadata,
                        authors: null,
                        stagedAt: Date.now(),
                    });
                    resolveDocumentAuthors({content: previewContent})
                        .then((authors) => {
                            const current = useReportStore.getState().previewDraft;
                            if (current?.reportId === reportIdStr && current?.stagedAt) {
                                useReportStore.getState().setPreviewDraft({
                                    ...current,
                                    authors,
                                });
                            }
                        })
                        .catch((err) => {
                            console.warn('[useViewToggle] failed to resolve preview authors', err);
                        });
                } catch (err) {
                    console.error('Failed to stage preview draft', err);
                }
            }
        } else if (reportId) {
            useReportStore.getState().clearPreviewDraft(String(reportId));
        }

        navigate(target, {replace: goingToPreview});
    }, [reportId, isPreview, navigate, setContent, getEditorContent]);

    return {
        isPreview,
        handleViewToggle
    };
}
